import { EnvelopeIcon, UserIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';

import { HighFiveButton } from '@/components/users/HighFiveButton';
import {
  userApiClient,
  useUpdateUserExtraMutation,
} from '@/services/api/userService';
import type { User } from '@/services/types/user-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * User entity type (re-export from service types)
 */
export type UserEntity = User;

/**
 * User tooltip content component
 * Pure presentation component - memoized for performance
 */
export const UserTooltipContent = memo<EntityTooltipContentProps<UserEntity>>(
  ({ fetchState }) => {
    const [updateUserExtra] = useUpdateUserExtraMutation();

    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={1} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="user" />;
    }

    // Handle idle or empty state - no data yet
    if (fetchState.status === 'idle') {
      return <EmptyState entityType="user" />;
    }

    // Success state - TypeScript knows fetchState.data exists
    const user = fetchState.data;
    const { first_name, last_name, email, username, avatar, extra } = user;

    const fullName =
      [first_name, last_name].filter(Boolean).join(' ') || username;

    const handleHighFive = (newValue: number) => {
      updateUserExtra({
        userId: user.id,
        extra: { ...extra, high_fives: newValue },
      });
    };

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader
          icon={UserIcon}
          title={fullName}
          badges={
            <HighFiveButton
              size="tiny"
              currentValue={extra?.high_fives || 0}
              onUpdate={handleHighFive}
            />
          }
        />

        {/* Avatar and Email Section */}
        {(avatar || email) && (
          <TooltipSection withBorder>
            {avatar && (
              <div className="mb-3 flex justify-center">
                <img
                  src={avatar}
                  alt={fullName}
                  className="h-16 w-16 rounded-full object-cover ring-2 ring-gray-200"
                />
              </div>
            )}
            {email && (
              <InfoRow icon={EnvelopeIcon} label="Email" value={email} />
            )}
          </TooltipSection>
        )}
      </div>
    );
  },
);

UserTooltipContent.displayName = 'UserTooltipContent';

/**
 * Hoverable user link with tooltip preview
 *
 * Shows user name, admin status, and email on hover.
 * Click to navigate to user detail page.
 *
 * @example
 * ```tsx
 * <UserLink entityId={userId}>
 *   John Doe
 * </UserLink>
 * ```
 */
export const UserLink = createEntityLink<UserEntity, string>({
  tooltipContent: UserTooltipContent,
  getDetailUrl: (id) => `/users/${id}`,
  useQuery: userApiClient.endpoints.getUserById.useQuery,
  getQueryArg: (id) => id,
  displayName: 'UserLink',
});
